import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  MapPinIcon,
  RocketLaunchIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolidIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import apiClient from '../../api/client';

export default function ApplySchool() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [contentKey, setContentKey] = useState(0);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    adminEmail: '',
    schoolName: '',
    schoolPhone: '',
    schoolAddress: '',
  });

  const firstInputRef = useRef(null);

  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [step]);

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // ── Navigation ──
  const nextStep = () => {
    if (step === 1) {
      if (!form.firstName || !form.lastName || !form.adminEmail) {
        toast.error('Please fill in all admin details.');
        return;
      }
      if (!form.adminEmail.includes('@')) {
        toast.error('Please enter a valid email address.');
        return;
      }
    }
    if (step === 2) {
      if (!form.schoolName || !form.schoolPhone || !form.schoolAddress) {
        toast.error('Please fill in all school details.');
        return;
      }
    }
    if (step < 3) {
      setStep(step + 1);
      setContentKey(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setContentKey(prev => prev + 1);
    }
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      name: form.schoolName,
      email: form.adminEmail,
      phone: form.schoolPhone,
      address: form.schoolAddress,
      adminFirstName: form.firstName,
      adminLastName: form.lastName,
    };

    try {
      const res = await apiClient.post('/schools', payload);

      if (res.data?.success) {
        const { school, admin } = res.data.data;
        setSuccess(true);
        toast.success(`🎉 School created! Code: ${school.code}`);

        setTimeout(() => {
          navigate('/onboarding/success', {
            state: {
              school: {
                name: school.name,
                code: school.code,
                email: school.email,
                phone: school.phone,
                address: school.address,
              },
              admin: {
                email: admin.email,
                tempPassword: admin.tempPassword,
                firstName: admin.firstName,
                lastName: admin.lastName,
              },
            },
          });
        }, 2000);
      } else {
        toast.error(res.data?.message || 'Failed to create school');
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || 'Server error while creating school'
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Step configuration ──
  const steps = [
    { id: 1, icon: UserIcon, label: 'Admin' },
    { id: 2, icon: BuildingOfficeIcon, label: 'School' },
    { id: 3, icon: RocketLaunchIcon, label: 'Launch' },
  ];

  // ── Render step content ──
  const renderStep = () => {
    const inputWrapper = (icon, label, children) => (
      <div className="relative">
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {label}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
            {icon}
          </div>
          {children}
        </div>
      </div>
    );

    switch (step) {
      case 1:
        return (
          <div className="space-y-5 animate-fade-slide">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600">
                <UserIcon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Admin Account</h2>
                <p className="text-sm text-slate-500">The administrator will manage this school.</p>
              </div>
            </div>

            {inputWrapper(
              <UserIcon className="w-5 h-5" />,
              'First Name',
              <input
                ref={firstInputRef}
                type="text"
                value={form.firstName}
                onChange={(e) => updateForm('firstName', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition outline-none"
                placeholder="FIRST NAME"
                required
              />
            )}

            {inputWrapper(
              <UserIcon className="w-5 h-5" />,
              'Last Name',
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => updateForm('lastName', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition outline-none"
                placeholder="LAST NAME"
                required
              />
            )}

            {inputWrapper(
              <EnvelopeIcon className="w-5 h-5" />,
              'Email Address',
              <input
                type="email"
                value={form.adminEmail}
                onChange={(e) => updateForm('adminEmail', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition outline-none"
                placeholder="ENTER YOUR EMAIL"
                required
              />
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-5 animate-fade-slide">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600">
                <BuildingOfficeIcon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">School Details</h2>
                <p className="text-sm text-slate-500">Tell us about your school.</p>
              </div>
            </div>

            {inputWrapper(
              <BuildingOfficeIcon className="w-5 h-5" />,
              'School Name',
              <input
                ref={firstInputRef}
                type="text"
                value={form.schoolName}
                onChange={(e) => updateForm('schoolName', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition outline-none"
                placeholder="ENTER SCHOOL NAME"
                required
              />
            )}

            {inputWrapper(
              <PhoneIcon className="w-5 h-5" />,
              'Phone Number',
              <input
                type="tel"
                value={form.schoolPhone}
                onChange={(e) => updateForm('schoolPhone', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition outline-none"
                placeholder="ENTER PHONE NUMBER"
                required
              />
            )}

            {inputWrapper(
              <MapPinIcon className="w-5 h-5" />,
              'School Address',
              <textarea
                value={form.schoolAddress}
                onChange={(e) => updateForm('schoolAddress', e.target.value)}
                rows="2"
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition resize-none outline-none"
                placeholder="ENTER SCHOOL ADDRESS HERE"
                required
              />
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-5 animate-fade-slide">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600">
                <RocketLaunchIcon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Confirm & Launch</h2>
                <p className="text-sm text-slate-500">Review your details below.</p>
              </div>
            </div>

            <div className="bg-primary-50 border border-primary-200 rounded-lg p-5 space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <UserIcon className="w-4 h-4 text-primary-500 mt-0.5" />
                <div>
                  <span className="font-medium">Admin:</span> {form.firstName} {form.lastName} ({form.adminEmail})
                </div>
              </div>
              <div className="flex items-start gap-2">
                <BuildingOfficeIcon className="w-4 h-4 text-primary-500 mt-0.5" />
                <div>
                  <span className="font-medium">School:</span> {form.schoolName}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <PhoneIcon className="w-4 h-4 text-primary-500 mt-0.5" />
                <div>
                  <span className="font-medium">Phone:</span> {form.schoolPhone}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPinIcon className="w-4 h-4 text-primary-500 mt-0.5" />
                <div>
                  <span className="font-medium">Address:</span> {form.schoolAddress}
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-400 flex items-center gap-1">
              <CheckCircleIcon className="w-3 h-3" /> By creating the school, you agree to our Terms of Service.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // ── Main render ──
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-20 relative overflow-hidden">
      {/* Decorative shapes – flat style */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-40 h-40 bg-primary-100/50 rounded-full blur-2xl" />
        <div className="absolute bottom-10 right-10 w-60 h-60 bg-secondary-100/50 rounded-full blur-2xl" />
      </div>

      <div className="w-full max-w-xl relative z-10">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 md:p-10">

          {/* ── Step Indicator (flat, no header) ── */}
          <div className="flex items-center justify-between mb-8 px-2">
            {steps.map((s, idx) => (
              <div key={s.id} className="flex flex-col items-center flex-1">
                <div className="relative flex items-center w-full">
                  {idx > 0 && (
                    <div
                      className={`absolute left-0 top-1/2 -translate-y-1/2 h-0.5 w-full transition-colors ${
                        s.id <= step ? 'bg-primary-500' : 'bg-slate-200'
                      }`}
                      style={{ left: '-50%', width: '100%' }}
                    />
                  )}
                  <div
                    className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 border-2 ${
                      s.id < step
                        ? 'bg-primary-500 border-primary-500 text-white'
                        : s.id === step
                        ? 'border-primary-500 bg-white text-primary-500 ring-2 ring-primary-200'
                        : 'border-slate-300 bg-white text-slate-400'
                    }`}
                  >
                    {s.id < step ? (
                      <CheckSolidIcon className="w-5 h-5" />
                    ) : (
                      <s.icon className="w-5 h-5" />
                    )}
                  </div>
                </div>
                <span
                  className={`text-[10px] font-medium mt-1 transition-colors ${
                    s.id <= step ? 'text-primary-600' : 'text-slate-400'
                  }`}
                >
                  {s.label}
                </span>
              </div>
            ))}
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit}>
            <div key={contentKey} className="transition-all duration-300">
              {renderStep()}
            </div>

            {/* ── Navigation Buttons ── */}
            <div className="flex justify-between mt-8 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={prevStep}
                className={`px-4 py-2 rounded-lg border-2 border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-primary-300 transition flex items-center gap-1 group ${
                  step === 1 ? 'invisible' : ''
                }`}
              >
                <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" /> Back
              </button>

              {step < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center gap-1 group shadow-sm"
                >
                  Next <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center gap-2 disabled:opacity-70 shadow-sm"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : success ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5 animate-bounce" /> Created!
                    </>
                  ) : (
                    <>
                      <RocketLaunchIcon className="w-5 h-5" /> Launch School
                    </>
                  )}
                </button>
              )}
            </div>
          </form>

          <p className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-300 animate-pulse" />
            Powered by Bilify SL LTD. • Sierra Leone
          </p>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeSlide {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-slide {
          animation: fadeSlide 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
}